import {
  Edit,
  SimpleForm,
  Toolbar,
  SaveButton,
  DeleteWithConfirmButton,
  useTranslate,
  useRedirect,
  useNotify,
} from 'react-admin';
import { Box, Typography, IconButton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { ModelConfigForm } from './ModelConfigForm';
import { transformModelConfig } from './modelConfigFormHelpers';
import { resolveErrorCode } from './errorMapping';

const EditToolbar = () => {
  const translate = useTranslate();
  const notify = useNotify();
  return (
    <Toolbar
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: '#FAFAFA',
        borderTop: '1px solid #E0E0E0',
        px: { xs: 2, sm: 3 },
        py: 2,
      }}
    >
      <SaveButton variant="contained" />
      {/* Single-record delete only (no bulk delete in the list). Backend rejects deleting the
          current default for its role with AI_MODEL_CONFIG_IN_USE (409, REF-08 §4). */}
      <DeleteWithConfirmButton
        mutationOptions={{
          onError: (error: any) => {
            const code = resolveErrorCode(error);
            if (code === 'AI_MODEL_CONFIG_IN_USE') {
              notify(
                translate('resources.ai-model-configs.errors.inUse', {
                  _: 'This model is the default for its role; set another default first.',
                }),
                { type: 'error' },
              );
            } else {
              notify(
                error?.message ||
                  translate('resources.ai-model-configs.notifications.error.delete', {
                    _: 'Failed to delete model config.',
                  }),
                { type: 'error' },
              );
            }
          },
        }}
      />
    </Toolbar>
  );
};

const PageHeader = () => {
  const redirect = useRedirect();
  const translate = useTranslate();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
      <IconButton
        onClick={() => redirect('list', 'ai-model-configs')}
        size="large"
        sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        title={translate('resources.ai-model-configs.edit.backToList', { _: 'Back to model configs' })}
      >
        <ArrowBack />
      </IconButton>
      <Box>
        <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
          {translate('resources.ai-model-configs.edit.title', { _: 'Edit Model Config' })}
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }} color="text.secondary">
          {translate('resources.ai-model-configs.edit.subtitle', {
            _: 'Refine this AI model configuration',
          })}
        </Typography>
      </Box>
    </Box>
  );
};

// Edit an AI model config. `params` JSON is pre-filled by the form's `format` (serializes the
// record's params object back to pretty JSON). provider/modelName/role are editable; the backend
// re-validates uniqueness on save. Same conflict handling as Create (AI_MODEL_CONFIG_CONFLICT ->
// friendly message; user kept on the form). Pessimistic mode so errors surface before refresh.
export const ModelConfigEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  return (
    <Edit
      title={false}
      component="div"
      mutationMode="pessimistic"
      transform={transformModelConfig}
      mutationOptions={{
        onSuccess: () => {
          notify('resources.ai-model-configs.notifications.updated', { type: 'success' });
          redirect('list', 'ai-model-configs');
        },
        onError: (error: any) => {
          const code = resolveErrorCode(error);
          if (code === 'AI_MODEL_CONFIG_CONFLICT') {
            notify(
              translate('resources.ai-model-configs.errors.conflict', {
                _: 'A model config with this provider + model + role already exists, or a default conflict occurred. Please review and try again.',
              }),
              { type: 'error' },
            );
          } else {
            notify(
              error?.message ||
                translate('resources.ai-model-configs.notifications.error.update', {
                  _: 'Failed to update model config.',
                }),
              { type: 'error' },
            );
          }
        },
      }}
    >
      <SimpleForm toolbar={<EditToolbar />}>
        <Box sx={{ width: '100%' }}>
          <PageHeader />
          <ModelConfigForm />
        </Box>
      </SimpleForm>
    </Edit>
  );
};

export default ModelConfigEdit;
